import { AfterViewInit, Component, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { environment } from '@core/environments/environment';
import { ClarityService } from '@core/services/clarity.service';
import { HubspotChatService } from '@core/services/hubspot-chat.service';
import { ScreenshotService } from '@core/services/screenshot.service';
import { EPermissions } from '@enums/permissions.enum';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import html2canvas from 'html2canvas-pro';
import { firstValueFrom } from 'rxjs';
import { EBugReportType } from './bug-report.interface';
import { BugReportService } from './bug-report.service';

@Component({
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    TranslateModule,
    DotsSpinnerComponent,
    HasPermissionDirective,
    IconComponent,
  ],
  selector: 'app-bug-report-button',
  styles: [
    `
      :host {
        position: fixed;
        bottom: 36px;
        right: 36px;
        z-index: 999999;

        ::ng-deep {
          .mat-mdc-button-touch-target {
            width: 48px !important;
            height: 48px !important;
          }
        }

        .bug-report-button {
          background-color: var(--shikenso-blue-500);

          img {
            filter: invert(1);
          }

          &:hover {
            background-color: var(--shikenso-blue-300);

            img {
              filter: invert(0);
            }
          }
        }
      }
    `,
  ],
  template: `
    <button
      mat-fab
      class="bug-report-button !rounded-full !w-12 !h-12 relative"
      aria-label="Bug report"
      *hasPermission="EPermissions.userFeedbackCreate"
      [matMenuTriggerFor]="menu"
      (click)="onBugReportButtonClick()"
    >
      <div class="flex items-center justify-center">
        @if (takingScreenshot() || hubspotChatService.loading()) {
          <app-dots-spinner color="white" />
        } @else {
          <app-icon src="icons/user-feedback.svg" alt="Give feedback" class="w-5 h-5" />
        }
      </div>
    </button>

    <mat-menu #menu="matMenu" class="bug-report-button-menu">
      <!-- <button mat-menu-item (click)="onOptionSelect(EBugReportType.bug)">
        <div class="flex items-center gap-2">
          <app-icon src="icons/debug.svg" alt="Report a bug" class="w-4 h-4" />
          <span>{{ 'shared.bugReport.reportBug' | translate }}</span>
        </div>
      </button> -->
      <button mat-menu-item (click)="onOptionSelect(EBugReportType.feedback)">
        <div class="flex items-center gap-2">
          <app-icon src="icons/user-feedback.svg" alt="Give feedback" class="w-4 h-4" />
          <span>{{ 'shared.bugReport.feedback' | translate }}</span>
        </div>
      </button>
      <button mat-menu-item (click)="onOptionSelect(EBugReportType.contact)">
        <div class="flex items-center gap-2">
          <app-icon src="icons/email.svg" alt="Contact" class="w-4 h-4" />
          <span>{{ 'shared.bugReport.contact' | translate }}</span>
        </div>
      </button>
      @if (isHubspotChatEnabled) {
        <button mat-menu-item (click)="onChatOptionSelect()">
          <div class="flex items-center gap-2">
            <app-icon src="icons/chat.svg" alt="Chat" class="w-4 h-4" />
            <span>{{ 'shared.bugReport.chat' | translate }}</span>
          </div>
        </button>
      }
    </mat-menu>
  `,
})
export class BugReportButtonComponent implements AfterViewInit {
  public readonly takingScreenshot = signal(false);
  public readonly EPermissions = EPermissions;
  /** Whether the "Chat" menu option is shown to users at all - test environment only by default. */
  protected readonly isHubspotChatEnabled = environment.hubspot?.enabled ?? false;

  public EBugReportType = EBugReportType;

  private bugReportButtonDisplay: string | null = null;

  constructor(
    private readonly bugReportService: BugReportService,
    private readonly clarityService: ClarityService,
    private readonly imageUtilitiesService: ImageUtilitiesService,
    private readonly screenshotService: ScreenshotService,
    protected readonly hubspotChatService: HubspotChatService,
  ) {}

  public ngAfterViewInit(): void {
    const bugReportButton = document.querySelector('.bug-report-button') as HTMLElement | null;
    this.bugReportButtonDisplay = bugReportButton?.style.display ?? null;
  }

  private async captureScreenshot(): Promise<string | null> {
    this.prepareScreenshot();

    try {
      const fullCanvas = await this.captureCanvas();
      const viewportCanvas = this.extractViewportFromCanvas(fullCanvas);
      const blob = await this.convertCanvasToBlob(viewportCanvas);
      const imageId = await this.uploadScreenshotFile(blob);

      this.cleanupScreenshot();

      return imageId;
    } catch (error) {
      this.handleScreenshotError(error);

      return null;
    }
  }

  private prepareScreenshot(): void {
    this.takingScreenshot.set(true);
    // this.toggleBugElements(true);
    this.screenshotService.isCapturing.set(true);
  }

  private async waitForIconsToLoad(): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, 300));
  }

  private async captureCanvas(): Promise<HTMLCanvasElement> {
    await this.waitForIconsToLoad();
    // Convert external images to data URLs to avoid CORS and canvas tainting
    await this.preprocessExternalImages();
    // Wait for all images to load after conversion
    await this.waitForImagesToLoad();

    this.toggleBugElements(true);

    const devicePixelRatio = window.devicePixelRatio || 1;
    const scale = Math.max(2, devicePixelRatio);

    const options: Record<string, unknown> = {
      allowTaint: false,
      backgroundColor: '#ffffff',
      height: window.innerHeight,
      imageTimeout: 15000,
      logging: false,
      removeContainer: true,
      scale,
      useCORS: true,
      width: window.innerWidth,
    };

    const canvas = await html2canvas(document.body, options as Parameters<typeof html2canvas>[1]);
    this.screenshotService.isCapturing.set(false);

    return canvas;
  }

  private async preprocessExternalImages(): Promise<void> {
    const images = Array.from(document.querySelectorAll('img'));
    const promises: Promise<void>[] = [];

    images.forEach((img) => {
      const imageSrc = img.src || img.getAttribute('src');

      if (!imageSrc || imageSrc.startsWith('data:') || imageSrc.startsWith('blob:')) {
        return;
      }

      try {
        const imgUrl = new URL(imageSrc, window.location.href);

        if (imgUrl.origin !== window.location.origin) {
          promises.push(this.convertImageToDataUrl(img, imageSrc));
        }
      } catch {
        // If URL parsing fails, try to convert it anyway
        promises.push(this.convertImageToDataUrl(img, imageSrc));
      }
    });

    await Promise.allSettled(promises);
  }

  private async convertImageToDataUrl(img: HTMLImageElement, imageSrc: string): Promise<void> {
    if (img.src.startsWith('data:')) {
      return;
    }

    try {
      const proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(imageSrc)}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(proxyUrl, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return;
      }

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        return;
      }

      const dataUrl = await this.blobToDataUrl(blob);

      if (dataUrl) {
        img.src = dataUrl;
      }
    } catch {
      // If proxy fails, leave image as is
    }
  }

  private async blobToDataUrl(blob: Blob): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      const reader = new FileReader();

      reader.onload = (): void => {
        if (typeof reader.result === 'string') {
          // Verify the data URL can be loaded as an image
          const newImg = new Image();

          newImg.onload = (): void => resolve(reader.result as string);
          newImg.onerror = (): void => resolve(null);
          newImg.src = reader.result as string;
        } else {
          resolve(null);
        }
      };

      reader.onerror = (): void => resolve(null);
      reader.readAsDataURL(blob);
    });
  }

  private async waitForImagesToLoad(): Promise<void> {
    const images = Array.from(document.querySelectorAll('img'));
    const imagePromises = images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalHeight !== 0) {
            resolve();

            return;
          }

          const timeout = setTimeout(() => resolve(), 3000);
          img.onload = (): void => {
            clearTimeout(timeout);
            resolve();
          };
          img.onerror = (): void => {
            clearTimeout(timeout);
            resolve();
          };
        }),
    );

    await Promise.allSettled(imagePromises);
    // Give extra time for images to fully render
    await new Promise((resolve) => setTimeout(resolve, 300));
  }

  private extractViewportFromCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const devicePixelRatio = window.devicePixelRatio || 1;
    const scale = Math.max(2, devicePixelRatio);
    const scaledWidth = window.innerWidth * scale;
    const scaledHeight = window.innerHeight * scale;
    const scaledScrollX = window.scrollX * scale;
    const scaledScrollY = window.scrollY * scale;

    const viewportCanvas = document.createElement('canvas');
    viewportCanvas.width = window.innerWidth;
    viewportCanvas.height = window.innerHeight;
    const ctx = viewportCanvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Use image smoothing for better quality when scaling down
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      canvas,
      scaledScrollX,
      scaledScrollY,
      scaledWidth,
      scaledHeight,
      0,
      0,
      window.innerWidth,
      window.innerHeight,
    );

    return viewportCanvas;
  }

  private convertCanvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (canvasBlob) => {
          if (canvasBlob) {
            resolve(canvasBlob);
          } else {
            reject(new Error('Failed to convert canvas to blob'));
          }
        },
        'image/png',
        1.0,
      );
    });
  }

  private async uploadScreenshotFile(blob: Blob): Promise<string> {
    const file = new File([blob], 'screenshot.png', { type: 'image/png' });

    return firstValueFrom(this.imageUtilitiesService.uploadImage(file));
  }

  private cleanupScreenshot(): void {
    this.toggleBugElements(false);
  }

  private handleScreenshotError(error: unknown): void {
    // eslint-disable-next-line no-console
    console.error('Screenshot capture failed:', error);
    this.screenshotService.isCapturing.set(false);
    this.toggleBugElements(false);
    this.takingScreenshot.set(false);
  }

  public onOptionSelect(type: EBugReportType): void {
    this.takingScreenshot.set(true);

    setTimeout(() => {
      this.captureScreenshot().then((imageId) => {
        this.openBugReportDialog(type, imageId);
      });
    });
  }

  public onBugReportButtonClick(): void {
    this.clarityService.trackEvent('bug_report_button_click', {
      source: 'floating_button',
    });
  }

  public onChatOptionSelect(): void {
    this.clarityService.trackEvent('bug_report_chat_option_click', {
      source: 'floating_button',
    });
    this.hubspotChatService.openChat();
  }

  private openBugReportDialog(type: EBugReportType, imageId: string | null): void {
    this.bugReportService.openBugReportDialog(type, imageId);
    this.takingScreenshot.set(false);
  }

  private toggleBugElements(hide: boolean): void {
    const bugReportButton = document.querySelector('.bug-report-button') as HTMLElement | null;
    const bugReportMenu = document.querySelector('.bug-report-button-menu') as HTMLElement | null;

    if (bugReportButton) {
      bugReportButton.style.display = hide ? 'none' : (this.bugReportButtonDisplay ?? '');
    }

    if (bugReportMenu) {
      bugReportMenu.style.display = 'none';
    }
  }
}
